import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * ResendInviteIconBtn — one-click send/resend of the interview invite from
 * any calendar card (month/day/schedule/list rows) without opening the full
 * `InterviewDetailDialog`. Delegates to POST /interviews/{id}/send-invite,
 * which Phase 158f wired to the real dispatcher (ICS + method=REQUEST/CANCEL).
 *
 * Props:
 *   interview  — row with `id`, `candidate_id`, `scheduled_at`, `invite_sent_at`
 *   onSent     — optional callback with the server response so the parent list
 *                can mirror `invite_sent_at` locally without a full reload.
 *   compact    — true (default) renders a 20px icon-only pill; false renders
 *                a short labelled pill ("Send" / "Resend") for taller rows.
 */
export default function ResendInviteIconBtn({ interview, onSent, compact = true, className = "" }) {
    const [busy, setBusy] = useState(false);
    const canSend = !!interview?.id && !!interview?.candidate_id && !!interview?.scheduled_at;
    const label = interview?.invite_sent_at ? "Resend" : "Send";

    const run = async (ev) => {
        if (ev) ev.stopPropagation(); // don't bubble to the card's own click
        if (busy) return;
        if (!canSend) {
            toast.error(
                !interview?.candidate_id
                    ? "Assign a candidate before sending the invite"
                    : "Set a scheduled time before sending the invite"
            );
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/interviews/${interview.id}/send-invite`, { note: "" });
            if (data?.ok === false) {
                toast.warning(
                    data?.report?.reason
                        ? `Invite accepted but delivery not confirmed: ${data.report.reason}`
                        : "Invite accepted but delivery not confirmed"
                );
            } else {
                toast.success(`Invite ${interview?.invite_sent_at ? "resent" : "sent"} to candidate + interviewers`);
            }
            onSent?.(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    if (compact) {
        return (
            <button
                type="button"
                onClick={run}
                disabled={!canSend || busy}
                className={`inline-flex items-center justify-center p-0.5 border border-ikb bg-white hover:bg-ikb/10 text-ikb disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
                data-testid={`cal-resend-${interview?.id}`}
                title={canSend
                    ? (interview?.invite_sent_at
                        ? `Last sent ${new Date(interview.invite_sent_at).toLocaleString()} — click to resend`
                        : "Send calendar invite to candidate + interviewers")
                    : "Assign a candidate and schedule time to enable"}
                aria-label={`${label} invite`}
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={run}
            disabled={!canSend || busy}
            className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border bg-ikb hover:bg-[#001f7a] text-white border-ikb disabled:opacity-50 ${className}`}
            data-testid={`cal-resend-${interview?.id}`}
            title={interview?.invite_sent_at
                ? `Last sent ${new Date(interview.invite_sent_at).toLocaleString()} — click to resend`
                : "Send the invite to candidate + interviewers"}
        >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {label}
        </button>
    );
}
